import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import PhotoGallery from '../components/PhotoGallery';
import styles from '../assets/propertyPhotos.module.css';
import { saveDraft, isQuotaError } from '../lib/offlineDb';
import { triggerSync, CHANGE_EVENT } from '../lib/inspectionSyncEngine';

const OFFLINE_MAX_SIZE = 25 * 1024 * 1024; // 25MB cap for offline buffering
const genUuid = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

const PropertyPhotos = () => {
  const { id: propertyId } = useParams();

  // State management
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isClient, setIsClient] = useState(false);
  const [error, setError] = useState(null);

  // Form states
  const [mainPhotoFile, setMainPhotoFile] = useState(null);
  const [mainPhotoPreview, setMainPhotoPreview] = useState(null);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [photoFields, setPhotoFields] = useState({});

  // Upload states
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100 across the whole batch
  const [uploadedCount, setUploadedCount] = useState(0); // photos confirmed saved so far
  const [uploadTotal, setUploadTotal] = useState(0); // total additional photos this batch

  // UI states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [photosPerPage] = useState(12);

  // Gallery (large image viewer) state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  // Helper function to get full image URL from backend
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/images/default-avatar.png';

    // If already a full URL (presigned URL or CDN), return as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // If it's an S3 key path, generate presigned URL via backend
    // Otherwise, prepend backend storage URL
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/storage/photos/${imagePath}`;
  };

  // Load property photos
  useEffect(() => {
    loadPropertyPhotos();
  }, [propertyId]);

  // Refresh the grid when an offline batch finishes syncing in the background,
  // so newly-uploaded photos appear without a manual page refresh.
  useEffect(() => {
    let t = null;
    const onChange = () => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      // Debounce: the engine emits several change events during a sync run.
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        loadPropertyPhotos();
      }, 600);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      if (t) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const loadPropertyPhotos = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setIsClient(user.role === 'CLIENT');

      // Get photos from backend API
      // Laravel equivalent: GET /admin/properties/{id}/photos -> PropertyPhotosController@manage
      const response = await api.get(`/properties/${propertyId}/photos`);

      if (response.data.success) {
        const photosData = response.data.data || [];
        setPhotos(photosData);

        // Initialize photo fields for management view
        if (!isClient && photosData.length > 0) {
          const fields = {};
          photosData.forEach(photo => {
            fields[photo.id] = {
              public: photo.public || false,
              client_can_see: photo.client_can_see || false,
              rotate: photo.rotate || 0,
            };
          });
          setPhotoFields(fields);
        }

        // Use property data from photos response if available (includes main_photo with presigned URL)
        if (response.data.property) {
          setProperty(response.data.property);
        }
      }

      // Get property details (fallback if not in photos response)
      if (!response.data?.property) {
        try {
          const propResponse = await api.get(`/properties/${propertyId}`);
          if (propResponse.data.success) {
            setProperty(propResponse.data.data);
          }
        } catch (propError) {
          console.warn('Could not load property details:', propError);
        }
      }

    } catch (error) {
      console.error('Error loading property photos:', error);
      setError('Failed to load property photos. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle main photo selection
  const handleMainPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMainPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setMainPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);

      toast.success('Main photo selected');
    }
  };

  // Handle multiple photos selection
  const handleNewPhotosChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNewPhotoFiles(files);
      toast.success(`${files.length} photo(s) selected`);
    }
  };

  // Handle photo field changes
  const handlePhotoFieldChange = (photoId, field, value) => {
    setPhotoFields(prev => ({
      ...prev,
      [photoId]: {
        ...prev[photoId],
        [field]: value,
      },
    }));
  };

  // Handle photo rotation
  const handlePhotoRotate = (photoId, direction) => {
    const currentRotation = photoFields[photoId]?.rotate || 0;
    const newRotation = direction === 'left'
      ? (currentRotation - 90 + 360) % 360
      : (currentRotation + 90) % 360;

    handlePhotoFieldChange(photoId, 'rotate', newRotation);
  };

  // Save photos (upload new photos using FormData)
  const handleSavePhotos = async (e) => {
    e.preventDefault();

    if (!mainPhotoFile && newPhotoFiles.length === 0) {
      toast.warning('Please select at least one photo before saving.');
      return;
    }

    setUploadingPhotos(true);

    // Buffer the selected photos for background sync. Returns true if queued.
    // Accepts explicit files so a partial failure can queue only what's left
    // (e.g. main photo already uploaded, only remaining chunks need queueing).
    const queuePhotosOffline = async (
      mainFile = mainPhotoFile,
      photoFiles = newPhotoFiles
    ) => {
      const tooBig = [mainFile, ...photoFiles]
        .filter(Boolean)
        .find((f) => f.size > OFFLINE_MAX_SIZE);
      if (tooBig) {
        toast.error(
          'While offline, each photo must be under 25MB. Upload larger photos when you’re back online.'
        );
        return false;
      }
      try {
        await saveDraft({
          id: genUuid(),
          propertyId: String(propertyId),
          reportType: 'propertyPhotos',
          clientUuid: genUuid(),
          status: 'queued',
          mainPhoto: mainFile
            ? {
                blob: mainFile,
                fileName: mainFile.name,
                mimeType: mainFile.type,
              }
            : null,
          photos: photoFiles.map((f) => ({
            blob: f,
            fileName: f.name,
            mimeType: f.type,
          })),
          attempts: 0,
          permanent: false,
          lastError: null,
        });
      } catch (err) {
        toast.error(
          isQuotaError(err)
            ? 'This device is out of storage, so the photos could not be saved for offline upload.'
            : 'Offline storage is unavailable, so the photos cannot be saved while offline.'
        );
        return false;
      }
      toast.info(
        'Saved. Your photos will upload automatically when you’re back online.'
      );
      triggerSync();
      setMainPhotoFile(null);
      setMainPhotoPreview(null);
      setNewPhotoFiles([]);
      return true;
    };

    // Offline: queue straight away.
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await queuePhotosOffline();
      setUploadingPhotos(false);
      return;
    }

    // Chunked upload: send additional photos in small groups so no single
    // request is large enough to trip a proxy/server timeout, a failure only
    // affects one chunk, and server memory never holds the whole batch at once.
    const CHUNK_SIZE = 10;          // photos per request
    const MAX_RETRIES = 2;          // per-request retries on timeout/network error
    const REQUEST_TIMEOUT = 300000; // 5 min ceiling per request (main photo or chunk)

    const chunks = [];
    for (let i = 0; i < newPhotoFiles.length; i += CHUNK_SIZE) {
      chunks.push(newPhotoFiles.slice(i, i + CHUNK_SIZE));
    }

    // Weighted byte progress across the main photo + every chunk.
    const mainBytes = mainPhotoFile ? mainPhotoFile.size : 0;
    const batchBytes = newPhotoFiles.reduce((sum, f) => sum + f.size, 0);
    const totalBytes = mainBytes + batchBytes || 1;
    let bytesSentBefore = 0; // bytes fully sent in completed request(s)

    // One request with bounded retries. Only timeout/network errors (no server
    // response) are retried; a genuine 4xx/5xx is thrown immediately.
    const putWithRetry = async (url, formData) => {
      for (let attempt = 0; ; attempt++) {
        try {
          await api.put(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: REQUEST_TIMEOUT,
            onUploadProgress: (e) => {
              const overall = Math.round(((bytesSentBefore + e.loaded) / totalBytes) * 100);
              setUploadProgress(Math.min(overall, 100));
            },
          });
          return;
        } catch (err) {
          const retriable = !err?.response; // includes ECONNABORTED (timeout)
          if (retriable && attempt < MAX_RETRIES) continue;
          throw err;
        }
      }
    };

    setUploadProgress(0);
    setUploadedCount(0);
    setUploadTotal(newPhotoFiles.length);

    let uploadedCount = 0; // additional photos confirmed saved on the server

    try {
      // Upload main photo if selected
      if (mainPhotoFile) {
        const mainPhotoFormData = new FormData();
        mainPhotoFormData.append('main_photo', mainPhotoFile);
        mainPhotoFormData.append('property_id', propertyId);

        await putWithRetry(`/properties/${propertyId}`, mainPhotoFormData);
        bytesSentBefore += mainBytes;
      }

      // Upload additional photos chunk by chunk.
      // Laravel equivalent: PUT /admin/properties/{id}/photos -> PropertyPhotosController@update
      for (const chunk of chunks) {
        const chunkBytes = chunk.reduce((sum, f) => sum + f.size, 0);
        const photosFormData = new FormData();
        chunk.forEach((file) => photosFormData.append('photos', file));

        await putWithRetry(`/properties/${propertyId}/photos`, photosFormData);

        bytesSentBefore += chunkBytes;
        uploadedCount += chunk.length;
        setUploadedCount(uploadedCount); // live count for the UI
      }

      toast.success(
        uploadedCount > 0
          ? `${uploadedCount} photo(s) successfully uploaded`
          : 'Photos successfully uploaded'
      );

      // Reset form
      setMainPhotoFile(null);
      setMainPhotoPreview(null);
      setNewPhotoFiles([]);

      // Reload photos
      await loadPropertyPhotos();

    } catch (error) {
      console.error('Error saving photos:', error);

      // Photos in earlier chunks are already saved; only these still need uploading.
      const remaining = newPhotoFiles.slice(uploadedCount);
      const isNetwork = !error?.response; // timeout or connection lost

      // Offline / connection lost mid-batch → buffer the REMAINING photos for
      // background sync (main photo, if any, already succeeded above).
      if (isNetwork && typeof navigator !== 'undefined' && !navigator.onLine) {
        const queued = await queuePhotosOffline(null, remaining);
        if (queued) {
          await loadPropertyPhotos(); // show the chunks that did land
          setUploadingPhotos(false);
          setUploadProgress(0);
          return;
        }
      }

      // Otherwise report partial progress and keep the unsent photos selected so
      // the user can retry just those (avoids re-uploading already-saved chunks).
      if (uploadedCount > 0) {
        setNewPhotoFiles(remaining);
        setMainPhotoFile(null);
        setMainPhotoPreview(null);
        await loadPropertyPhotos();
        toast.error(
          `${uploadedCount} photo(s) uploaded, but ${remaining.length} failed` +
            (isNetwork ? ' (upload timed out).' : '.') +
            ' The remaining photos are still selected — click Save to retry them.'
        );
      } else {
        toast.error(
          isNetwork
            ? 'Upload timed out. Try again, or select fewer photos at once.'
            : error.response?.data?.message || 'Failed to save photos. Please try again.'
        );
      }
    } finally {
      setUploadingPhotos(false);
      setUploadProgress(0);
    }
  };

  // Save photo fields (public, client_can_see, rotate)
  const handleSavePhotoFields = async (e) => {
    e.preventDefault();

    try {
      const photoData = { photos: photoFields };
      console.log('📸 [FRONTEND] Saving photo fields:', JSON.stringify(photoData, null, 2));

      // Laravel equivalent: PUT /admin/properties/{id}/photos/fields -> PropertyPhotosController@updateFields
      const response = await api.put(`/properties/${propertyId}/photos/fields`, photoData);
      console.log('📸 [FRONTEND] Save response:', response.data);

      // Show appropriate message based on whether photos were rotated
      if (response.data?.data?.photos_rotated > 0) {
        toast.success(`Photos updated! (${response.data.data.photos_rotated} photo(s) rotated)`);
      } else {
        toast.success('Photos updated!');
      }

      // Reset rotation values to 0 after save (since the actual image is now rotated in S3)
      const resetFields = {};
      Object.keys(photoFields).forEach(photoId => {
        resetFields[photoId] = {
          ...photoFields[photoId],
          rotate: 0, // Reset rotation since image is now physically rotated
        };
      });
      setPhotoFields(resetFields);

      // Reload photos to reflect changes and get fresh presigned URLs
      await loadPropertyPhotos();

    } catch (error) {
      console.error('Error updating photo fields:', error);
      toast.error('Failed to update photos. Please try again.');
    }
  };

  // Delete photo
  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;

    try {
      // Delete from backend (backend handles file cleanup)
      // Laravel equivalent: GET /admin/properties/{id}/photos/{photoId}/destroy -> PropertyPhotosController@destroy
      await api.get(`/properties/${propertyId}/photos/${photoToDelete.id}/destroy`);

      toast.success('Photo successfully deleted');

      // Remove photo from state
      setPhotos(prev => prev.filter(photo => photo.id !== photoToDelete.id));

      // Close modal
      setShowDeleteModal(false);
      setPhotoToDelete(null);

    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to delete photo. Please try again.');
    }
  };

  // Gallery slides — derived from the full photos array (not paginated), so
  // prev/next in the lightbox spans every photo on the property.
  const gallerySlides = photos.map((p) => ({
    src: getImageUrl(p.name),
    title: (p.name || '').split('?')[0].split('/').pop() || `Photo ${p.id}`,
  }));

  // Mirror photoFields rotation into the gallery so rotating in the lightbox
  // updates the grid thumbnail (and vice versa). photoFields is the source of
  // truth; the user persists with the existing "Save" button on the grid.
  const galleryRotations = photos.reduce((acc, p, i) => {
    const r = photoFields[p.id]?.rotate;
    if (r) acc[i] = r;
    return acc;
  }, {});

  const handleGalleryRotate = (slideIdx) => {
    const photo = photos[slideIdx];
    if (!photo) return;
    const current = photoFields[photo.id]?.rotate || 0;
    handlePhotoFieldChange(photo.id, 'rotate', (current + 90) % 360);
  };

  const openGalleryAtPhoto = (photo) => {
    const idx = photos.findIndex((p) => p.id === photo.id);
    setGalleryIndex(idx >= 0 ? idx : 0);
    setGalleryOpen(true);
  };

  // Pagination
  const indexOfLastPhoto = currentPage * photosPerPage;
  const indexOfFirstPhoto = indexOfLastPhoto - photosPerPage;
  const currentPhotos = photos.slice(indexOfFirstPhoto, indexOfLastPhoto);
  const totalPages = Math.ceil(photos.length / photosPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render rotatable image component
  const renderRotatableImage = (photo) => {
    const rotation = photoFields[photo.id]?.rotate || 0;
    const imageStyle = {
      transform: `rotate(${rotation}deg)`,
      transition: 'transform 0.3s ease',
    };

    return (
      <div className={styles.rotatableImage} data-rotate={rotation}>
        <div
          className={styles.rotatableImagePreview}
          onClick={() => openGalleryAtPhoto(photo)}
          style={{ cursor: 'zoom-in' }}
          title="Click to view larger"
        >
          <img src={getImageUrl(photo.name)} alt="Property photo" style={imageStyle} />
        </div>

        <div className={styles.rotatableImageActions}>
          {!isClient && (
            <>
              <button
                type="button"
                className={styles.rotatableImageHandle}
                onClick={() => handlePhotoRotate(photo.id, 'right')}
                title="Rotate"
              >
                <i className="fa fa-repeat" aria-hidden="true"></i>
              </button>

              <button
                type="button"
                className={styles.documentActionDelete}
                onClick={() => {
                  setPhotoToDelete(photo);
                  setShowDeleteModal(true);
                }}
                title="Remove"
              >
                Remove
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render photo item for management view
  const renderPhotoItem = (photo) => {
    return (
      <div key={photo.id} className={styles.photoGridItem}>
        <div className={styles.textCenter}>
          {renderRotatableImage(photo)}

          <div className={styles.checkboxGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.formCheckbox}
                checked={photoFields[photo.id]?.public || false}
                onChange={(e) => handlePhotoFieldChange(photo.id, 'public', e.target.checked)}
              />
              Public
            </label>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                className={styles.formCheckbox}
                checked={photoFields[photo.id]?.client_can_see || false}
                onChange={(e) => handlePhotoFieldChange(photo.id, 'client_can_see', e.target.checked)}
              />
              Client
            </label>
          </div>
        </div>
      </div>
    );
  };

  // Extract filename from path (handles both S3 keys and presigned URLs)
  const getFilenameFromPath = (path) => {
    if (!path) return '';
    
    // If it's a presigned URL, extract the key from the URL first
    if (path.includes('?X-Amz-')) {
      // Presigned URL format: https://bucket.s3.region.amazonaws.com/key?X-Amz-...
      const urlParts = path.split('?')[0]; // Remove query parameters
      const parts = urlParts.split('/');
      return parts[parts.length - 1] || 'photo.jpg';
    }
    
    // Regular S3 key path
    const parts = path.split('/');
    return parts[parts.length - 1] || 'photo.jpg';
  };

  // Find main photo
  const getMainPhoto = () => {
    // First check photos array (from getPropertyPhotos endpoint - includes presigned URLs)
    const mainPhotoFromArray = photos.find(photo => photo.is_main_photo === true || photo.id === 'main');
    if (mainPhotoFromArray && mainPhotoFromArray.name) {
      console.log('📸 [FRONTEND] Main photo found in photos array:', {
        id: mainPhotoFromArray.id,
        url: mainPhotoFromArray.name.substring(0, 100) + '...',
        isPresignedUrl: mainPhotoFromArray.name.startsWith('https://')
      });
      return {
        url: mainPhotoFromArray.name, // Already a presigned URL from backend
        source: 'photos_array'
      };
    }

    // Fallback to property.main_photo (from getById endpoint - also includes presigned URLs)
    if (property?.main_photo) {
      console.log('📸 [FRONTEND] Main photo found in property object:', {
        url: property.main_photo.substring(0, 100) + '...',
        isPresignedUrl: property.main_photo.startsWith('https://')
      });
      return {
        url: property.main_photo, // Already a presigned URL from backend
        source: 'property_field'
      };
    }

    console.log('⚠️ [FRONTEND] No main photo found', { photosCount: photos.length, hasProperty: !!property });
    return null;
  };

  // Render main photo section
  const renderMainPhotoSection = () => {
    const mainPhoto = getMainPhoto();

    return (
      <div className={styles.formGroup}>
        <div className={styles.colSm3}>
          <label className={styles.controlLabel}>Main photo</label>
        </div>
        <div className={styles.colSm5}>
          <div className={styles.rotatableImage}>
            {/* Current Main Photo Preview (if exists) */}
            {mainPhoto && !mainPhotoPreview && (
              <>
                <div className={styles.currentPhotoSection}>
                  <div className={styles.currentPhotoLabel}>
                    <strong>Current main photo:</strong>
                  </div>
                  <div className={styles.simpleImagePreview}>
                    <img
                      src={getImageUrl(mainPhoto.url)}
                      alt="Current main photo"
                      onError={(e) => {
                        console.error('Failed to load main photo:', mainPhoto.url);
                        e.target.onerror = null;
                        e.target.src = '/images/default-property.jpg';
                      }}
                      onLoad={() => {
                        console.log('Main photo loaded successfully:', mainPhoto.url);
                      }}
                    />
                  </div>

                  <div className={styles.imageInfo}>
                    <small className={styles.filename}>
                      <i className="fa fa-file-image-o" aria-hidden="true"></i>
                      {' '}{getFilenameFromPath(mainPhoto.url)}
                    </small>
                  </div>
                </div>

                <div className={styles.photoSeparator}>
                  <hr />
                  <span>or replace with new photo:</span>
                </div>
              </>
            )}

            {/* New photo preview */}
            {mainPhotoPreview && (
              <div className={styles.newPhotoPreview}>
                <div className={styles.currentPhotoLabel}>
                  <strong>New main photo (not saved yet):</strong>
                </div>
                <div className={styles.simpleImagePreview}>
                  <img src={mainPhotoPreview} alt="New main photo preview" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMainPhotoFile(null);
                    setMainPhotoPreview(null);
                  }}
                  className={styles.btnSmall}
                >
                  Remove
                </button>
              </div>
            )}

            {/* File Upload Section */}
            <div className={styles.uploadSection}>
              <input
                type="file"
                accept="image/*"
                onChange={handleMainPhotoChange}
                className={styles.formControl}
              />
              <small className={styles.helpText}>
                {mainPhoto
                  ? "Choose a new photo to replace the current main photo. Maximum file size: 100MB"
                  : "Upload main property photo. Allowed formats: JPEG, PNG, GIF. Maximum file size: 100MB"
                }
              </small>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className={styles.pagination}>
        <button
          className={styles.paginationBtn}
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
          <button
            key={number}
            className={`${styles.paginationBtn} ${currentPage === number ? styles.active : ''}`}
            onClick={() => paginate(number)}
          >
            {number}
          </button>
        ))}

        <button
          className={styles.paginationBtn}
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>

        <span className={styles.paginationInfo}>
          Page {currentPage} of {totalPages}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          Loading property photos...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          {error}
          <button
            className={styles.btn}
            onClick={loadPropertyPhotos}
            style={{ marginLeft: '10px' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <Link to="/properties">Dashboard</Link>
        <span className={styles.separator}>/</span>
        <Link to="/properties">Properties</Link>
        <span className={styles.separator}>/</span>
        <Link to={`/properties/${propertyId}`}>{property?.name || 'Property'}</Link>
        <span className={styles.separator}>/</span>
        <span className={styles.currentPage}>Photos</span>
      </div>

      {!isClient ? (
        // Management view for staff
        <>
          {/* Manage Photos Panel */}
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              <h3>Manage Photos of Property</h3>
            </div>
            <div className={styles.panelBody}>
              <form onSubmit={handleSavePhotos}>
                <fieldset className={styles.formHorizontal}>
                  {renderMainPhotoSection()}

                  <div className={styles.formGroup}>
                    <div className={styles.colSm3}>
                      <label className={styles.controlLabel}>Photos (may choose multiple)</label>
                    </div>
                    <div className={styles.colSm5}>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleNewPhotosChange}
                        className={styles.formControl}
                      />
                      <small className={styles.helpText}>
                        Allowed formats: JPEG, PNG, GIF. Maximum file size: 100MB per photo (up to 50 photos)
                      </small>
                      {uploadingPhotos && (
                        <div className={styles.uploadProgress}>
                          <div className={styles.progressBarTrack}>
                            <div
                              className={styles.progressBarFill}
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <small>
                            {uploadProgress < 100
                              ? `Uploading photos... ${uploadProgress}%`
                              : 'Processing on server...'}
                            {uploadTotal > 0 && (
                              <> — {uploadedCount} of {uploadTotal} uploaded</>
                            )}
                          </small>
                        </div>
                      )}
                      {newPhotoFiles.length > 0 && !uploadingPhotos && (
                        <div className={styles.uploadedFiles}>
                          <small>
                            {newPhotoFiles.length} photo(s) selected and ready to save
                          </small>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <div className={styles.colSm3}></div>
                    <div className={styles.colSm5}>
                      <button
                        type="submit"
                        className={styles.btnHead}
                        disabled={uploadingPhotos || (!mainPhotoFile && newPhotoFiles.length === 0)}
                      >
                        <i className="glyphicon glyphicon-ok"></i>
                        {uploadingPhotos ? 'Uploading...' : 'Save photos'}
                      </button>
                      {!mainPhotoFile && newPhotoFiles.length === 0 && (
                        <small className={styles.helpText} style={{ display: 'block', marginTop: '5px', color: '#999' }}>
                          Select photos above to enable saving
                        </small>
                      )}
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
          </div>

          {/* Photos Panel */}
          <div className={styles.panel}>
            <div className={styles.panelHeading}>
              Photos
            </div>
            <div className={styles.panelBody}>
              {photos.length === 0 ? (
                <p className={styles.textCenter}>No photos.</p>
              ) : (
                <>
                  <form onSubmit={handleSavePhotoFields}>
                    <div className={styles.row}>
                      {currentPhotos.map(renderPhotoItem)}
                    </div>

                    <div className={styles.textCenter}>
                      <button type="submit" className={styles.btnHead}>
                        <i className="glyphicon glyphicon-ok"></i> Save
                      </button>
                    </div>
                  </form>

                  {renderPagination()}
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        // Client view
        <div className={styles.panel}>
          <div className={styles.panelHeading}>
            <h3>Photos</h3>
          </div>
          <div className={styles.panelBody}>
            {photos.length === 0 ? (
              <p className={styles.ml20}>There is no photo of the property.</p>
            ) : (
              <>
                <div className={styles.row}>
                  {currentPhotos.map(photo => (
                    <div key={photo.id} className={styles.photoGridItem}>
                      <div className={styles.textCenter}>
                        <div>
                          <button
                            type="button"
                            onClick={() => openGalleryAtPhoto(photo)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'zoom-in',
                            }}
                            title="Click to view larger"
                          >
                            <img
                              src={getImageUrl(photo.name)}
                              className={styles.imgProperty}
                              alt={`Property photo ${photo.id}`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {renderPagination()}
              </>
            )}
          </div>
        </div>
      )}

      {/* Large image viewer — controlled rotation so the grid thumbnail
          tracks the lightbox rotation and the existing Save flow persists it. */}
      <PhotoGallery
        open={galleryOpen}
        close={() => setGalleryOpen(false)}
        slides={gallerySlides}
        index={galleryIndex}
        rotations={galleryRotations}
        onRotate={handleGalleryRotate}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Confirm Delete</h3>
              <button
                className={styles.modalClose}
                onClick={() => {
                  setShowDeleteModal(false);
                  setPhotoToDelete(null);
                }}
              >
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <p>Are you sure you want to remove? This action cannot be undone.</p>
            </div>
            <div className={styles.modalFooter}>
              <button
                className={styles.btn}
                onClick={() => {
                  setShowDeleteModal(false);
                  setPhotoToDelete(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDeletePhoto}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyPhotos;
